import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { map, switchMap, tap, withLatestFrom } from 'rxjs/operators';

import { PolicySelectors } from '@amfam/policy/data-access';
import { QuickQuoteEmailTemplates } from '@amfam/policy/models';
import * as quickQuoteDataActions from '@amfam/policy/replace-vehicle/data-access';
import { quickQuoteDataQuery } from '@amfam/policy/replace-vehicle/data-access';
import { VehicleActionTypes } from '@amfam/policy/vehicle/data-access';
import { BrandSelectors } from '@amfam/shared/utility/brand';

import { QuickQuoteFeatureService } from '../../services/quick-quote-feature.service';
import {
  AnalyticsEventType,
  ReplaceVehiclePaymentsFrequency,
  ReplaceVehicleQuickQuoteContext,
  ReplaceVehicleQuickQuoteOperation
} from './quick-quote-enum';
import { sendAnalytics } from './quick-quote-feature.actions';
import { QuickQuoteFeatureState } from './quick-quote-feature.reducer';
import {
  getQuickQuoteEmailInfo,
  getReplaceVehicleId,
  selectContext
} from './quick-quote-feature.selectors';

@Injectable()
export class QuickQuoteFeatureEffects {
  constructor(
    private actions$: Actions,
    private quickQuoteService: QuickQuoteFeatureService,
    private store: Store<QuickQuoteFeatureState>,
    private router: Router
  ) {}

  quickQuoteSuccess$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(quickQuoteDataActions.getQuickQuoteSuccess),
      withLatestFrom(this.store.select(getQuickQuoteEmailInfo)),
      map(([_, emailParams]) =>
        this.quickQuoteService.prepareCustomerEmail(
          QuickQuoteEmailTemplates.REPLACE_VEHICLE_QUICKQUOTE,
          emailParams
        )
      ),
      map(email => quickQuoteDataActions.quickQuoteSendCustomerEmail({ email }))
    );
  });

  quickQuoteFail$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(quickQuoteDataActions.getQuickQuoteFailed),
        withLatestFrom(this.store.select(getReplaceVehicleId)),
        tap(([_, vehicleId]) =>
          this.router.navigate(
            [`/policies/risk-selection/replace-vehicle-quick-quote/${vehicleId}/agent-help`],
            {
              state: {
                analyticsEventType: AnalyticsEventType.REPLACE_VEHICLE_QUOTE_SPINNER_NEED_AGENT,
                operation: ReplaceVehicleQuickQuoteOperation.REPLACE_VEHICLE_ERROR_SEND_AGENT
              }
            }
          )
        )
      );
    },
    { dispatch: false }
  );

  redirectOnQuickQuoteFailed$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(quickQuoteDataActions.bindQuickQuoteFail, quickQuoteDataActions.bindQuoteFail),
        withLatestFrom(
          this.store.select(getReplaceVehicleId),
          this.store.select(quickQuoteDataQuery.getFuturePayments)
        ),
        tap(([_, vehicleId, futurePayments]) =>
          this.router.navigateByUrl(
            `/policies/risk-selection/replace-vehicle-quick-quote/${vehicleId}/agent-help`,
            {
              state: {
                analyticsEventType:
                  !futurePayments || futurePayments.length === 0
                    ? AnalyticsEventType.REPLACE_VEHICLE_ONLINE_BIND_ERROR
                    : AnalyticsEventType.REPLACE_VEHICLE_FUTURE_PAYMENTS_NEED_AGENT,
                paymentFrequency:
                  futurePayments?.length > 1
                    ? ReplaceVehiclePaymentsFrequency.MONTHLY_PAY
                    : ReplaceVehiclePaymentsFrequency.FULL_PAY
              }
            }
          )
        )
      );
    },
    { dispatch: false }
  );

  redirectQuickQuoteSuccess$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(quickQuoteDataActions.getQuickQuoteSuccess, quickQuoteDataActions.getQuoteSuccess),
        withLatestFrom(this.store.select(getReplaceVehicleId), this.store.select(selectContext)),
        tap(([_, vehicleId, context]) => {
          const url = `/policies/risk-selection/replace-vehicle-quick-quote/${vehicleId}/${
            context === ReplaceVehicleQuickQuoteContext.QUICK_QUOTE ? 'quick-quote' : 'quote'
          }`;
          this.router.navigateByUrl(url);
        })
      );
    },
    { dispatch: false }
  );

  redirectSendToAgentSuccess$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(
          quickQuoteDataActions.ansNotificationOnErrorSuccess,
          quickQuoteDataActions.ansNotificationSuccess,
          quickQuoteDataActions.bindQuickQuoteSuccess,
          quickQuoteDataActions.ansNotificationOnBindSuccess
        ),
        withLatestFrom(
          this.store.select(getReplaceVehicleId),
          this.store.select(selectContext),
          this.store.select(quickQuoteDataQuery.getFuturePayments)
        ),
        tap(([_, vehicleId, context, futurePayments]) => {
          if (context === ReplaceVehicleQuickQuoteContext.QUICK_QUOTE) {
            this.router.navigateByUrl(
              `/policies/risk-selection/replace-vehicle-quick-quote/${vehicleId}/notification`
            );
          } else {
            this.router.navigateByUrl(
              `/policies/risk-selection/replace-vehicle-quick-quote/${vehicleId}/confirmation`,
              {
                state: {
                  paymentFrequency:
                    futurePayments?.length > 1
                      ? ReplaceVehiclePaymentsFrequency.MONTHLY_PAY
                      : ReplaceVehiclePaymentsFrequency.FULL_PAY
                }
              }
            );
          }
        })
      );
    },
    { dispatch: false }
  );

  redirectToUnderWritingQuestions$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(VehicleActionTypes.LookupVinYearSuccessType),
        withLatestFrom(
          this.store.select(getReplaceVehicleId),
          this.store.select(BrandSelectors.selectIsPartner),
          this.store.select(PolicySelectors.selectHasClassicAutoPolicies),
          this.store.select(PolicySelectors.selectHasAdvanceAutoPolicies),
          this.store.select(PolicySelectors.selectHasOnlyPolicyComprehensiveCoverage)
        ),
        switchMap(
          ([
            ,
            vehicleId,
            isPartner,
            hasClassicAutoPolicy,
            hasAdvanceAutoPolicy,
            hasOnlyPolicyComprehensiveCoverage
          ]) => {
            const isShowhardstopRiskSelectionPage =
              (hasClassicAutoPolicy && !hasAdvanceAutoPolicy && !isPartner) ||
              (hasAdvanceAutoPolicy && !isPartner && hasOnlyPolicyComprehensiveCoverage);
            return [
              this.router.navigateByUrl(
                isShowhardstopRiskSelectionPage && vehicleId
                  ? `/policies/risk-selection/replace-vehicle-quick-quote/${vehicleId}/agent-helps`
                  : `/policies/risk-selection/replace-vehicle-quick-quote/${vehicleId}/underwriting`
              )
            ];
          }
        )
      );
    },
    { dispatch: false }
  );

  /**
   * @author: Abhishek Singh
   * @returns:
   * @description: This effect triggers a analytics notification.
   */
  sendAnalytics$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(sendAnalytics),
        map(action => action.payload),
        map(payload => this.quickQuoteService.sendAnalytics(payload))
      );
    },
    { dispatch: false }
  );
}
